"use client";

import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "../../lib/utils";
import { type CardCollectionItem, CardItem } from "./card";

// GRID LAYOUT — uses container queries so the grid responds to its own
// container width, not the viewport width. This ensures correct column counts
// inside narrow parents (e.g. doc preview panes) as well as full-page layouts.
export const collectionGridVariants = cva("grid w-full", {
  variants: {
    size: {
      xs: "@2xl:grid-cols-3 @4xl:grid-cols-4 @6xl:grid-cols-5 @7xl:grid-cols-6 grid-cols-2",
      s: "@3xl:grid-cols-4 @5xl:grid-cols-5 @xl:grid-cols-3 grid-cols-2",
      m: "@3xl:grid-cols-3 @5xl:grid-cols-4 @sm:grid-cols-2 grid-cols-1",
      l: "@4xl:grid-cols-3 @sm:grid-cols-2 grid-cols-1",
      xl: "@2xl:grid-cols-2 grid-cols-1",
    },
    orientation: {
      vertical: "auto-rows-fr",
      horizontal: "!grid-cols-1",
    },
    variant: {
      card: "@sm:gap-6 gap-4",
      minimal: "gap-5",
    },
  },
  compoundVariants: [
    {
      orientation: "horizontal",
      variant: "card",
      class: "gap-4",
    },
    {
      orientation: "horizontal",
      variant: "minimal",
      class: "gap-2",
    },
  ],
  defaultVariants: { size: "m", orientation: "vertical", variant: "card" },
});

export interface CardCollectionProps
  extends VariantProps<typeof collectionGridVariants> {
  aspectRatio?: "square" | "video" | "portrait";
  clampDescription?: boolean | number;
  clampTitle?: boolean | number;
  className?: string;
  imageShape?: "square" | "circle";
  itemAs?: "article" | "div";
  items: CardCollectionItem[];
  limit?: number;
  // NEW: Collection-wide defaults for new features
  metaPosition?: "header" | "footer" | "above-title" | "below-title";
  onItemClick?: (item: CardCollectionItem, index: number) => void;
  renderAction?: (item: CardCollectionItem, index: number) => React.ReactNode;
  renderFooter?: (item: CardCollectionItem, index: number) => React.ReactNode;
  renderItem?: (item: CardCollectionItem, index: number) => React.ReactNode;
  renderMeta?: (meta: string, item: CardCollectionItem) => React.ReactNode;
  showSeparators?: boolean;
  titleStyle?: "simple" | "bold";
  variant?: "card" | "minimal";
}

export function CardCollection({
  items,
  size = "m",
  variant = "card",
  orientation = "vertical",
  aspectRatio = "square",
  imageShape = "square",
  titleStyle = "simple",
  limit,
  className,
  onItemClick,
  metaPosition = "above-title",
  renderMeta,
  renderAction,
  renderFooter,
  showSeparators = false,
  itemAs = "article",
  renderItem,
  clampTitle,
  clampDescription,
}: CardCollectionProps) {
  // Apply limit if specified
  const displayedItems = limit ? items.slice(0, limit) : items;

  return (
    <div className={cn("@container w-full", className)}>
      <div
        className={cn(collectionGridVariants({ size, orientation, variant }))}
      >
        {/* biome-ignore lint/complexity/noExcessiveCognitiveComplexity: complex item rendering with multiple display modes */}
        {displayedItems.map((item, index) => {
          // Allow custom render function
          if (renderItem) {
            return (
              <div key={item.id ?? `${item.title}-${index}`}>
                {renderItem(item, index)}
              </div>
            );
          }

          // Build enhanced item with custom renderers
          const enhancedItem: CardCollectionItem = {
            ...item,
            // Apply custom meta renderer if provided
            meta: renderMeta && item.meta ? undefined : item.meta,
            // Merge action from props
            action: item.action ?? renderAction?.(item, index),
            // Merge footer from props
            footer: item.footer ?? renderFooter?.(item, index),
          };

          // Inject rendered meta node into the appropriate slot
          if (renderMeta && item.meta) {
            const customMetaNode = renderMeta(item.meta, item);
            if (metaPosition === "header" && !enhancedItem.action) {
              enhancedItem.action = customMetaNode;
            } else if (metaPosition === "footer") {
              enhancedItem.footer = (
                <>
                  {customMetaNode}
                  {enhancedItem.footer}
                </>
              );
            } else {
              // above-title / below-title: pass as metaNode so CardItem renders it
              enhancedItem.metaNode = customMetaNode;
            }
          }

          return (
            <CardItem
              as={itemAs}
              aspectRatio={aspectRatio}
              clampDescription={clampDescription}
              clampTitle={clampTitle}
              imageShape={imageShape}
              item={enhancedItem}
              key={item.id ?? `${item.title}-${index}`}
              metaPosition={metaPosition}
              onClick={onItemClick ? () => onItemClick(item, index) : undefined}
              orientation={orientation ?? "vertical"}
              showSeparators={showSeparators}
              titleStyle={titleStyle}
              variant={variant}
            />
          );
        })}
      </div>
    </div>
  );
}

// Re-export CardCollectionItem type for convenience
export type { CardCollectionItem } from "./card";
